import { EDashboardShareRole } from "../types/dashboards";
import type {
  TDashboardPermissionChangedBodyKey,
  TDashboardSharedBodyKey,
} from "../types/notifications";

export const NOTIFICATIONS_QUERY_KEY = "notifications";
export const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY =
  "notifications-unread-count";

export const NOTIFICATIONS_DEFAULT_LIMIT = 20;
export const NOTIFICATIONS_MAX_LIMIT = 100;

// NOTE: Upper bound for the numeric unread-count badge before it collapses
// to "99+". Used by the avatar badge in UserMenu and the browser tab title
// hook on web; mobile can reuse the same threshold so every surface that
// renders the unread badge collapses identically.
export const NOTIFICATIONS_UNREAD_COUNT_DISPLAY_THRESHOLD = 99;

// NOTE: Tunables for the unread-count badge painted on top of the favicon
// (see apps/nsai/web/src/hooks/use-favicon-badge.ts). Web-only today, but
// kept here so every notification-related constant lives in one file.
// Sizes are in canvas pixels — the canvas is rendered at the CANVAS_SIZE
// below and the browser handles scaling to the actual tab favicon slot
// (16 or 32 device pixels).
export const NOTIFICATIONS_FAVICON_CANVAS_SIZE = 64;

// NOTE: Badge is a PILL (rounded rectangle, corner radius = height/2),
// NOT a circle — so "1" stays compact (square pill ≈ circle) and "99+"
// stretches into a wider pill instead of being chopped off or forcing a
// wastefully large circle. Height matches the old circle diameter (~44px
// on a 64px canvas) so visual weight is preserved; width auto-sizes to
// text + horizontal padding.
export const NOTIFICATIONS_FAVICON_BADGE_HEIGHT = 44;
export const NOTIFICATIONS_FAVICON_BADGE_PADDING_X = 6;

// NOTE: tailwind red-600 — same hue UserMenu's avatar badge uses.
export const NOTIFICATIONS_FAVICON_BADGE_FILL_COLOR = "#dc2626";
export const NOTIFICATIONS_FAVICON_BADGE_TEXT_COLOR = "#ffffff";
// NOTE: 28px keeps single digits punchy AND leaves room for "99+" within
// the pill width budget (canvas=64, max pill ≈ 54 → fits with corner
// overflow = 2px).
export const NOTIFICATIONS_FAVICON_BADGE_FONT = "bold 28px sans-serif";

// NOTE: Keep at 0 — anything positive paints the pill outside the canvas
// and the browser crops it. The "make the badge fit" knob is the brand
// inset below, not corner overflow: we shrink the brand toward the
// bottom-left so the badge has clear canvas space in the top-right.
export const NOTIFICATIONS_FAVICON_BADGE_CORNER_OVERFLOW_PX = 0;

// NOTE: Pixels the brand icon is shrunk from the canvas's top + right
// edges. The brand is drawn anchored to the bottom-left at
// (CANVAS_SIZE - INSET) × (CANVAS_SIZE - INSET), leaving an INSET-tall
// strip at the top and an INSET-wide strip at the right where the pill
// can sit without overlapping the logo's silhouette. Pick a value that
// frees roughly the pill's diameter — too small and the badge eats into
// the brand; too large and the brand reads as off-center.
export const NOTIFICATIONS_FAVICON_BRAND_INSET_PX = 10;

// NOTE: Must match apps/nsai/server/src/notifications/notifications.constants.ts
export const NOTIFICATIONS_WS_NAMESPACE = "/api/notifications";

export enum ENotificationWsEvent {
  NOTIFICATION_CREATED = "notification:created",
  // NOTE: Lightweight live-refresh signal for the call-assistant list. Fired on
  // every meeting status transition (not just terminal ones) so clients can
  // invalidate the meetings query instead of polling it. Carries only
  // meetingUuid + status — the client refetches for the authoritative row.
  MEETING_STATUS_CHANGED = "meeting:status-changed",
  // NOTE: Transient staged-progress signal for the background template
  // extraction job (NSAI-3862) — pushed to the uploader while a large deck is
  // analyzed. Terminal statuses tell the client to fetch the authoritative
  // result/error from the job status endpoint. Mirrors the server enum in
  // apps/nsai/server/src/notifications/notifications.constants.ts.
  PRESENTATION_EXTRACTION_PROGRESS = "presentation:extraction-progress",
  // NOTE: Transient live-refresh signal for organization ownership/role
  // changes (NSAI-3616). Deliberately separate from the
  // ORGANIZATION_OWNERSHIP_RECEIVED / ORGANIZATION_OWNER_REMOVED bell
  // notifications: those are preference-gated, so a user who muted the
  // ORGANIZATIONS category would never learn their owner-gated UI is stale and
  // would be stuck until a manual reload. This one is never persisted and
  // never consults preferences — muting a bell category must not leave the app
  // rendering permissions the server no longer grants. Mirrors the server enum
  // in apps/nsai/server/src/notifications/notifications.constants.ts.
  ORGANIZATION_ACCESS_CHANGED = "organization:access-changed",
}

// NOTE: NSAI-6940 — one body per share role rather than a role word dropped into
// one sentence: "Can view" / "Can edit" do not inflect the same way in every
// language, so each role gets a sentence its translators can write whole.
export const DASHBOARD_SHARED_BODY_KEYS: Record<
  EDashboardShareRole,
  TDashboardSharedBodyKey
> = {
  [EDashboardShareRole.VIEWER]: "type.dashboard_shared.body_viewer",
  [EDashboardShareRole.EDITOR]: "type.dashboard_shared.body_editor",
};

export const DASHBOARD_PERMISSION_CHANGED_BODY_KEYS: Record<
  EDashboardShareRole,
  TDashboardPermissionChangedBodyKey
> = {
  [EDashboardShareRole.VIEWER]: "type.dashboard_permission_changed.body_viewer",
  [EDashboardShareRole.EDITOR]: "type.dashboard_permission_changed.body_editor",
};
